import { Col, Modal, Row } from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import UploadComponent from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import ErrModal from 'components/molecules/SpecialniSluzby/ErrModal';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';

import fileDownload from 'services/fileDownload';
import { ETypPrevodu } from 'services/specialniSluzby/uePozadavky';
import { INacteni, ueSouborNacteni, ueZalozeni } from 'services/specialniSluzby/ueSoubor';

import routePathEnum from 'enums/routePathEnum';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import getLanguageObject from 'utils/getLanguageObject';

const NovySoubor: React.FC = () => {
    const { specialniSluzby, language, aktivniFirma } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [typPozadavku, setTypPozadavku] = useState<string>();
    const [fileData, setFileData] = useState<UploadChangeParam>();
    const [fileRespData, setFileRespData] = useState<INacteni>();
    const [zapsatBezchybne, setZapsatBezchybne] = useState<'OK' | 'POCETERR' | ''>();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalResp, setModalResp] = useState<boolean>(false);
    const [errModalMsg, setErrModalMsg] = useState<string>();
    const [pocetCP, setPocetCP] = useState<{ cpUspesne: number; cpNeuspesne: number }>();
    const [fileErr, setFileErr] = useState<string>('');
    const isDLT = specialniSluzby?.detail?.dlt;
    // const [modalUploadErr, setModalUploadErr] = useState<string>('');
    const [paginationChybnych, setPaginationChybnych] = useState<{
        offset: number;
        page: number;
        limit: number;
    }>({
        offset: 0,
        page: 1,
        limit: 10
    });
    const [paginationBezchybnych, setPaginationBezchybnych] = useState<{
        offset: number;
        page: number;
        limit: number;
    }>({
        offset: 0,
        page: 1,
        limit: 10
    });

    useEffect(() => {
        if (specialniSluzby) {
            setTypPozadavku(specialniSluzby.detail?.typ);
        }
        setErrStatus(0);
    }, [specialniSluzby]);

    const countPocetCP = (data: INacteni) => {
        let cpU = 0;
        let cpN = 0;

        data.sezpozbezchyby?.polozky.forEach(it => {
            if (it.pocetcp) cpU += it.pocetcp;
        });
        data.sezpozchybnych?.polozky.forEach(it => {
            if (it.pocetcp) cpN += it.pocetcp;
        });

        setPocetCP({
            cpUspesne: cpU,
            cpNeuspesne: cpN
        });
    };

    const columns = [
        {
            title: <TranslateText>UCET</TranslateText>,
            dataIndex: 'ucet',
            key: 'ucet'
        },
        ...(!isDLT
            ? [
                  {
                      title: <TranslateText>ID_NABYVATELE</TranslateText>,
                      dataIndex: 'idnabyvatele',
                      key: 'idnabyvatele'
                  }
              ]
            : []),
        {
            title: <TranslateText>POCET_CP</TranslateText>,
            dataIndex: 'pocetcp',
            key: 'pocetcp',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        ...(specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI
            ? [
                  {
                      title: <TranslateText>CASTKA</TranslateText>,
                      dataIndex: 'castka',
                      key: 'castka',
                      render: (val: string) => formatInputNumberThousand(val || 0)
                  }
              ]
            : []),
        ...(!isDLT
            ? [
                  {
                      title: <TranslateText>KOD_UCASTNIKA</TranslateText>,
                      dataIndex: 'koducastnika',
                      key: 'koducastnika'
                  }
              ]
            : []),
        {
            title: <TranslateText>CISLO_ZASTAVNI_SMLOUVY</TranslateText>,
            dataIndex: 'cislozastavnismlouvy',
            key: 'cislozastavnismlouvy'
        },
        ...(!isDLT
            ? [
                  {
                      title: <TranslateText>POZNAMKA</TranslateText>,
                      dataIndex: 'poznamka',
                      key: 'poznamka'
                  }
              ]
            : []),
        {
            title: <TranslateText>UPOZORNENI</TranslateText>,
            dataIndex: 'upozorneni',
            key: 'upozorneni'
        }
    ];

    const columnsSICAV = [
        {
            title: <TranslateText>UCET</TranslateText>,
            dataIndex: 'ucet',
            key: 'ucet'
        },
        {
            title: <TranslateText>ID_NABYVATELE</TranslateText>,
            dataIndex: 'idnabyvatele',
            key: 'idnabyvatele'
        },
        {
            title: <TranslateText>SS_DETAIL_FILTR_TYP</TranslateText>,
            dataIndex: 'typtransakce',
            key: 'typtransakce',
            render: (text: string) =>
                text && (
                    <TranslateText>
                        {text === 'NAKUP' ? 'SS_DETAIL_FILTR_NAKUP' : text === 'PRODEJ' ? 'SS_DETAIL_FILTR_PRODEJ' : ''}
                    </TranslateText>
                )
        },
        {
            title: <TranslateText>POCET_CP</TranslateText>,
            dataIndex: 'pocetcp',
            key: 'pocetcp',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        {
            title: <TranslateText>KOD_UCASTNIKA</TranslateText>,
            dataIndex: 'koducastnika',
            key: 'koducastnika'
        },
        {
            title: <TranslateText>POZNAMKA</TranslateText>,
            dataIndex: 'poznamka',
            key: 'poznamka'
        },
        {
            title: <TranslateText>UPOZORNENI</TranslateText>,
            dataIndex: 'upozorneni',
            key: 'upozorneni'
        }
    ];

    const renderModal = () => {
        return (
            <Modal
                title={
                    <TranslateText>
                        {zapsatBezchybne ? 'SS_NOVY_SOUBOR_MODAL_ZAPSAT_HEAD' : 'SS_NOVY_SOUBOR_MODAL_SMAZANI_HEAD'}
                    </TranslateText>
                }
                open={showModal}
                onCancel={() => {
                    setModalResp(false);
                    setShowModal(false);
                }}
                footer={[
                    <Row className="button-row-modal">
                        {modalResp || zapsatBezchybne === 'POCETERR' ? (
                            <ButtonComponent
                                buttontext={zapsatBezchybne === 'POCETERR' ? 'ZAVRIT' : 'POKRACOVAT'}
                                onClick={() => {
                                    setShowModal(false);
                                    zapsatBezchybne !== 'POCETERR' &&
                                        navigateWorkaround(
                                            `/firma/${aktivniFirma?.iducet}${
                                                isDLT
                                                    ? routePathEnum.SLUZBY_DETAIL_POZADAVKU_DLT
                                                    : routePathEnum.SLUZBY_DETAIL_POZADAVKU
                                            }`
                                        );
                                }}
                                id="pokracovatBtn"
                            />
                        ) : (
                            <>
                                <ButtonComponent
                                    id="anoBtn"
                                    buttontext="ANO"
                                    onClick={() => {
                                        /*
                                        Po stisknutí tlačítka Ano se zavolá metoda POST /UE/{id}/soubor/zapis (požadavky v tabulce "Seznam bezchybných požadavků" se zapíší do databáze) a zobrazí se informace o úspěšném zapsání (případně o chybě). Po zavření této informace upozornění následuje návrat na detail požadavku.
                                        */
                                        setShowModal(false);
                                        setLoading(true);
                                        if (zapsatBezchybne) {
                                            specialniSluzby?.detail?.id &&
                                                ueZalozeni(
                                                    specialniSluzby.detail.id,
                                                    {
                                                        file: fileData?.file.response.tempnazev,
                                                        nazev: fileData?.file.name || ''
                                                    },
                                                    isDLT
                                                )
                                                    .then(resp => {
                                                        if (resp.stav?.includes('TIMEOUT')) {
                                                            setErrModalMsg('SS_SLUZBA_ERROR_NESKORO');
                                                        } else {
                                                            setShowModal(true);
                                                            setModalResp(true);
                                                        }
                                                    })
                                                    .catch(err => {
                                                        console.log('MHO zalozeni error', err);
                                                    })
                                                    .finally(() => {
                                                        setLoading(false);
                                                    });
                                        } else {
                                            setShowModal(true);
                                            setModalResp(true);
                                        }
                                    }}
                                />
                                <ButtonComponent
                                    buttontext="NE"
                                    onClick={() => {
                                        setShowModal(false);
                                    }}
                                    id="neBtn"
                                />
                            </>
                        )}
                    </Row>
                ]}
            >
                <Row>
                    {modalResp ? (
                        <>
                            <Title
                                level={5}
                                text={
                                    zapsatBezchybne
                                        ? 'SS_NOVY_SOUBOR_MODAL_ZAPSAT_RESP'
                                        : 'SS_NOVY_SOUBOR_MODAL_SMAZANI_RESP'
                                }
                            />
                            {zapsatBezchybne && (
                                <div>
                                    <Paragraph text="SS_NOVY_SOUBOR_MODAL_ZAPSAT_RESP2" />
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <Paragraph
                                text={
                                    zapsatBezchybne === 'POCETERR'
                                        ? 'SS_NOVY_RUCNI_MODAL_RESP_PREKROCENO'
                                        : zapsatBezchybne
                                          ? fileRespData?.sezpozchybnych?.polozky.length
                                              ? 'SS_NOVY_SOUBOR_MODAL_ZAPSAT_TEXT'
                                              : 'SS_NOVY_SOUBOR_MODAL_ZAPSAT_TEXT_2'
                                          : 'SS_NOVY_SOUBOR_MODAL_SMAZANI_TEXT'
                                }
                            />
                        </>
                    )}
                </Row>
            </Modal>
        );
    };

    const renderFileImport = () => {
        if (fileRespData) return;

        return (
            <>
                <Row>
                    <span
                        style={{
                            paddingRight: '20px'
                        }}
                    >
                        <TranslateText>SS_NOVY_SOUBOR_NAZEV_SOUBORU</TranslateText>
                    </span>
                    <UploadComponent
                        id="uploadBtn"
                        showDownloadIcon={false}
                        onChange={(data: UploadChangeParam) => {
                            if (data.fileList.length) {
                                data?.file?.response && setFileErr('');
                                setFileData(data);
                            }
                        }}
                        fileList={(fileData?.fileList || []).map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev || '' : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })}
                        onRemove={() => {
                            setFileErr('');
                            setFileData(undefined);
                        }}
                        accept=".csv"
                        /* onDownload={(file: UploadFile) => {
                            console.log(file);
                            prepareFile(file.response.tempnazev ? file.response.tempnazev : '').then(resp => {
                                fileDownload(resp.redirect, file.name ? file.name : 'file');
                            });
                        }} */
                        extramessage={fileErr}
                        buttonDisabled={fileData && fileData.fileList.length > 0 ? true : false}
                    />
                </Row>
                <Row className="button-row">
                    <ButtonComponent
                        type="default"
                        id="zpetUploadBtn"
                        buttontext="ZPET"
                        onClick={() => {
                            navigateWorkaround(-1);
                        }}
                    />
                    <ButtonComponent
                        id="uploadSubmitBtn"
                        buttontext="POKRACOVAT"
                        disabled={!fileData?.file.response?.tempnazev}
                        onClick={() => {
                            if (!fileData?.file.response?.tempnazev) {
                                setFileErr('SOUBOR_POVINNY');
                            } else if (specialniSluzby && fileData && fileData.file.response?.tempnazev) {
                                setLoading(true);
                                ueSouborNacteni(
                                    specialniSluzby?.detail?.id!,
                                    {
                                        file: fileData.file.response?.tempnazev,
                                        nazev: fileData.file.name || ''
                                    },
                                    isDLT
                                )
                                    .then((resp: INacteni) => {
                                        setFileRespData(resp);
                                        countPocetCP(resp);
                                    })
                                    .catch((err: any) => {
                                        err.response?.data?.message &&
                                            setFileErr(
                                                err.response.data.message.includes('Duplicitni')
                                                    ? 'CHYBA_SOUBOR_DUPLICITNI'
                                                    : 'CHYBA_SOUBOR_NEVALIDNI'
                                            );
                                    })
                                    .finally(() => setLoading(false));
                            }
                        }}
                    />
                </Row>
            </>
        );
    };

    const renderResponse = () => {
        if (!fileRespData) return;

        return (
            <>
                <Title level={3} text={'SS_NOVY_SOUBOR_OBSAH_DAVKY'} />
                <Row>
                    <Col span={16}>
                        <InputComponent
                            name="nazevdavky1"
                            className="label-left"
                            label="SS_NOVY_SOUBOR_NAZEV_DAVKY"
                            notRequired={true}
                            defaultValue={fileData?.file.name}
                            disabled={true}
                            labelCol={{
                                span: 10
                            }}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col span={16}>
                        <InputComponent
                            name="nazevdavky2"
                            className="label-left"
                            label="SS_NOVY_SOUBOR_POCET_CHYBNYCH"
                            defaultValue={fileRespData.sezpozchybnych?.polozky.length || 0}
                            disabled={true}
                            notRequired={true}
                            labelCol={{
                                span: 10
                            }}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col span={16}>
                        <InputComponent
                            name="nazevdavky3"
                            className="label-left"
                            label="SS_NOVY_SOUBOR_POCET_BEZCHYBNYCH"
                            defaultValue={fileRespData.sezpozbezchyby?.polozky.length || 0}
                            disabled={true}
                            notRequired={true}
                            labelCol={{
                                span: 10
                            }}
                        />
                    </Col>
                </Row>

                {fileRespData.sezpozchybnych?.polozky.length ? (
                    <>
                        <Title
                            level={4}
                            text={`${getLanguageObject(language).SS_NOVY_SOUBOR_SEZNAM_NA}
                                        ${
                                            typPozadavku &&
                                            getLanguageObject(language)['SS_NOVY_POZADAVEK_NA_' + typPozadavku || '']
                                        }
                                        ${getLanguageObject(language).SS_NOVY_POZADAVEK_S_CHYBOU}
                                    `}
                        />
                        <TableComponent
                            rowKey={(_, index) => index || 0}
                            columns={specialniSluzby?.detail?.typ === 'SICAV' ? columnsSICAV : columns}
                            dataSource={fileRespData.sezpozchybnych?.polozky}
                            locale={{
                                emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                            }}
                            pagination={{
                                total: fileRespData.sezpozchybnych?.polozky.length,
                                onChange: page => {
                                    setPaginationChybnych({
                                        page: page,
                                        offset: (page - 1) * paginationChybnych.limit,
                                        limit: paginationChybnych.limit
                                    });
                                },
                                current: paginationChybnych.page,
                                showSizeChanger: false
                            }}
                        />
                        <FormWrapper onFinish={() => {}}>
                            <Row gutter={8}>
                                <Col>
                                    {pocetCP?.cpNeuspesne !== undefined && (
                                        <InputComponent
                                            name="pocetcp"
                                            label="POCET_CP"
                                            notRequired={true}
                                            defaultValue={formatInputNumberThousand(pocetCP.cpNeuspesne || 0)}
                                            disabled={true}
                                        />
                                    )}
                                </Col>
                                <Col>
                                    <InputComponent
                                        name="pocetuctu"
                                        label="SS_PREHLED_POCET_UCTU"
                                        notRequired={true}
                                        defaultValue={formatInputNumberThousand(
                                            fileRespData.sezpozchybnych?.polozky.length || 0
                                        )}
                                        disabled={true}
                                    />
                                </Col>
                            </Row>
                        </FormWrapper>
                    </>
                ) : null}

                {fileRespData.sezpozbezchyby?.polozky.length ? (
                    <>
                        <Title
                            level={4}
                            text={`${getLanguageObject(language).SS_NOVY_SOUBOR_SEZNAM_BEZ_CHYBY}
                                        ${
                                            typPozadavku &&
                                            getLanguageObject(language)['SS_NOVY_POZADAVEK_NA_' + typPozadavku || '']
                                        }
                                    `}
                        />
                        <TableComponent
                            rowKey={(_, index) => index || 0}
                            columns={specialniSluzby?.detail?.typ === 'SICAV' ? columnsSICAV : columns}
                            dataSource={fileRespData.sezpozbezchyby?.polozky}
                            locale={{
                                emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                            }}
                            pagination={{
                                total: fileRespData.sezpozbezchyby?.polozky.length,
                                onChange: page => {
                                    setPaginationBezchybnych({
                                        page: page,
                                        offset: (page - 1) * paginationBezchybnych.limit,
                                        limit: paginationBezchybnych.limit
                                    });
                                },
                                current: paginationBezchybnych.page,
                                showSizeChanger: false
                            }}
                        />{' '}
                        <FormWrapper onFinish={() => {}}>
                            <Row gutter={8}>
                                {pocetCP?.cpUspesne !== undefined && (
                                    <Col>
                                        <InputComponent
                                            name="pocetcp"
                                            label="POCET_CP"
                                            notRequired={true}
                                            defaultValue={formatInputNumberThousand(pocetCP.cpUspesne || 0)}
                                            disabled={true}
                                        />
                                    </Col>
                                )}
                                <Col>
                                    <InputComponent
                                        name="pocetuctu"
                                        label="SS_PREHLED_POCET_UCTU"
                                        notRequired={true}
                                        defaultValue={formatInputNumberThousand(
                                            fileRespData.sezpozbezchyby?.polozky.length || 0
                                        )}
                                        disabled={true}
                                    />
                                </Col>
                            </Row>
                        </FormWrapper>
                    </>
                ) : null}

                <Row className="button-row">
                    <ButtonComponent
                        id="zpetAltBtn"
                        type="default"
                        buttontext="ZPET"
                        onClick={() => {
                            setFileRespData(undefined);
                        }}
                    />
                    {fileRespData?.sezpozbezchyby?.polozky.length ? (
                        <ButtonComponent
                            id="zapsatBtn"
                            buttontext="SS_NOVY_SOUBOR_ZAPSAT_BEZCHYBNE"
                            onClick={() => {
                                setZapsatBezchybne(fileRespData.kontrolapocet === 'OK' ? 'OK' : 'POCETERR');
                                setShowModal(true);
                            }}
                        />
                    ) : null}
                    <ButtonComponent
                        id="smazatBtn"
                        buttontext="SS_NOVY_SOUBOR_SMAZAT_DAVKU"
                        onClick={() => {
                            setZapsatBezchybne('');
                            setShowModal(true);
                        }}
                    />
                    {fileRespData?.sezpozchybnych?.polozky.length ? (
                        <ButtonComponent
                            id="exportovatBtn"
                            buttontext="SS_NOVY_SOUBOR_EXPORTOVAT_CHYBNE"
                            onClick={() => {
                                setLoading(true);
                                specialniSluzby?.detail?.id &&
                                    fileDownload(
                                        fileRespData.nazeverror,
                                        // eslint-disable-next-line max-len
                                        `${getLanguageObject(language).SS_NOVY_SOUBOR_CHYBNE} - ${specialniSluzby?.detail?.isin}.csv`
                                    )
                                        .catch(err => {
                                            console.log('MHO zalozeni error', err);
                                        })
                                        .finally(() => {
                                            setLoading(false);
                                        });
                            }}
                        />
                    ) : null}
                </Row>
                {renderModal()}
            </>
        );
    };

    return (
        <LoadingWrapper loading={loading}>
            <Title
                level={4}
                text={`${getLanguageObject(language)['SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ]} - ${
                    getLanguageObject(language).ISIN
                }: ${specialniSluzby?.detail?.isin}, ${getLanguageObject(language).DATUM}: ${dayjs(
                    specialniSluzby?.detail?.datum
                ).format('DD.MM.YYYY')}`}
            />
            {errStatus ? (
                <Row>
                    <Col span={16}>
                        {errStatus === 404 ? (
                            <Paragraph text="RESPONSE_ERROR_404" />
                        ) : errStatus ? (
                            <Paragraph text="SLUZBA_NEDOSTUPNA" />
                        ) : null}
                        <Row className="button-row">
                            <ButtonComponent
                                id="zpetBtn"
                                type="default"
                                buttontext="ZPET"
                                onClick={() => {
                                    setErrStatus(0);
                                }}
                            />
                        </Row>
                    </Col>
                </Row>
            ) : (
                <>
                    {renderFileImport()}
                    {renderResponse()}
                    {/* {renderModalUpload()} */}
                </>
            )}
            <ErrModal modalMsg={errModalMsg} setModalMsg={setErrModalMsg} />
        </LoadingWrapper>
    );
};

export default NovySoubor;
